/**
 * ============================================================================
 * FILE: useCacheInvalidation.js
 * PATH: composables/queries/useCacheInvalidation.js
 * LINES: 353
 * ============================================================================
 * 
 * PURPOSE:
 * Centralized cache invalidation composable. Single source of truth for all
 * Vue Query cache invalidation operations. Ensures consistent cache management
 * across the application after mutations, data changes, or scenario switches.
 * 
 * ARCHITECTURE:
 *   Mutation/Data Change → useCacheInvalidation → Vue Query Client → Cache
 * 
 * DOMAIN: Vue Query / Cache Management
 * LAYER: Composable (Cache Layer)
 * 
 * ✅ SINGLE SOURCE OF TRUTH: All cache invalidation goes through here
 * 
 * ============================================================================
 * FUNCTIONS & LINE NUMBERS:
 * ============================================================================
 * 
 * useCacheInvalidation()                 Lines 16-317
 *   Main composable function
 *   Returns: { invalidateScenario, invalidateCosts, invalidateAll, ... }
 * 
 *   Invalidation Functions:
 *   - invalidateScenario(scenarioId, workspaceId, options) Lines 29-111
 *     Invalidates all caches for a specific scenario
 *     Options: { invalidateBaseline: boolean }
 *   
 *   - invalidateCosts(scenarioId, resultType)            Lines 119-142
 *     Invalidates only cost queries for a scenario
 *   
 *   - invalidateNetworkData(scenarioId, mode)            Lines 150-173
 *     Invalidates only network data queries for a scenario
 *   
 *   - invalidateAll()                                     Lines 179-188
 *     Nuclear option - invalidates all caches
 *   
 *   - invalidateAfterOptimization(scenarioId)             Lines 195-205
 *     Invalidates after optimization completes
 *   
 *   - invalidateAfterSimulation(scenarioId)               Lines 212-222
 *     Invalidates after simulation completes
 *   
 *   - invalidateAfterDataChange(scenarioId, workspaceId)  Lines 230-246
 *     Invalidates after data import/deletion
 *   
 *   - invalidateScenariosList(workspaceId)               Lines 253-278
 *     Invalidates scenarios list queries
 *   
 *   - invalidateCustomScenarios(workspaceId)             Lines 285-304
 *     Invalidates custom scenarios list
 * 
 * STANDALONE FUNCTION:
 * - invalidateAfterOptimization(queryClient, scenarioId, appStore) Lines 321-351
 *   Standalone function for use in mutation callbacks outside setup()
 *   Accepts queryClient as parameter (for use outside Vue context)
 * 
 * ============================================================================
 * DEPENDENCIES:
 * ============================================================================
 * 
 * External:
 * - @tanstack/vue-query (useQueryClient)
 * - @/stores/app (useAppStore)
 * - @/core/cache/useQueries (queryKeys)
 * 
 * ============================================================================
 * USAGE EXAMPLES:
 * ============================================================================
 * 
 * // Invalidate scenario after optimization
 * const { invalidateAfterOptimization } = useCacheInvalidation()
 * await invalidateAfterOptimization(scenarioId)
 * 
 * // Invalidate specific scenario
 * const { invalidateScenario } = useCacheInvalidation()
 * await invalidateScenario(scenarioId, workspaceId, { invalidateBaseline: true })
 * 
 * // Invalidate all caches
 * const { invalidateAll } = useCacheInvalidation()
 * await invalidateAll()
 * 
 * // Standalone function (for mutation callbacks)
 * import { invalidateAfterOptimization } from '@/core/cache'
 * await invalidateAfterOptimization(queryClient, scenarioId, appStore)
 * 
 * ============================================================================
 * CACHE INVALIDATION STRATEGY:
 * ============================================================================
 * 
 * 1. Invalidate queries using query keys (marks as stale)
 * 2. Refetch active queries (components currently using the data)
 * 3. Components automatically update when cache changes
 * 
 * Query keys follow pattern: ['queryType', scenarioId, ...otherParams]
 * Partial keys invalidate all variations (e.g., ['scenarioCosts', scenarioId])
 * 
 * ============================================================================
 */

import { useQueryClient } from '@tanstack/vue-query'
import { useAppStore } from '@/core/store'
// The three standalone invalidators below run outside any component — from a
// mutation callback, and from React since Phase 1D. They used to take the store
// as an argument for one call each; the Zustand actions are module singletons,
// so they can reach them directly (Phase 1B).
import { appActions, appStore as appState } from '@/core/state/app'
import { queryKeys } from './useQueries'

// ============================================================================
// SHARED INVALIDATION LOGIC - Used by both composable and standalone functions
// ============================================================================

/**
 * Internal helper: Invalidate the caches an optimization run affects, costs included
 * 
 * Shared logic used by both the composable and standalone versions
 * 
 * @param {object} queryClient - Vue Query client instance
 * @param {number} scenarioId - Scenario ID that was optimized
 * @param {object} appStore - App store instance
 */
async function _invalidateAfterOptimizationRunInternal(queryClient, scenarioId) {
  console.log(`🔄 [Cache] Invalidating related caches after optimization for scenario ${scenarioId} (costs included)`)
  
  // Set result type to optimization
  appActions.setCurrentResultType('optimization')
  
  // Costs are invalidated WITH everything else.
  //
  // They used to be excluded, because both callers wrote the optimization run's
  // POST response straight into the costs key first and this function was not
  // supposed to undo that. But the POST response and `getScenarioCosts`'s
  // normalized shape are NOT the same shape: the run returns `fulfillment_rate`
  // and no `service_level`, `open_dcs`, `demand_fulfilled`, `units_processed`,
  // `avg_capacity_utilization`, `risk_score` or `data_quality_score`. Every one
  // of those is read as `results?.x || 0` in `summary-statistics/stats.ts`, so
  // the missing fields were displayed as confident zeros — the Service Level
  // card read "0.0%" after a successful run that reported 100% fulfilment, and
  // nothing ever corrected it because this exclusion also skipped the refetch.
  //
  // One query key, one shape, one producer: `getScenarioCosts`. The raw write is
  // gone from both callers and the refetch below re-reads through the
  // normalizer. It costs one GET per run.
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['scenarioCosts', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['networkData', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['facilitiesBreakdown', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['purchaseMethodStats', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['scenarioBundle', scenarioId] })
  ])

  await queryClient.refetchQueries({
    predicate: (query) => {
      const key = query.queryKey
      if (!Array.isArray(key)) return false
      return (
        (key[0] === 'scenarioCosts' && key[1] === scenarioId) ||
        (key[0] === 'networkData' && key[1] === scenarioId) ||
        (key[0] === 'facilitiesBreakdown' && key[1] === scenarioId) ||
        (key[0] === 'purchaseMethodStats' && key[1] === scenarioId) ||
        (key[0] === 'scenarioBundle' && key[1] === scenarioId)
      )
    },
    type: 'active'
  })
  
  console.log(`✅ [Cache] Related caches invalidated, costs refetched through the normalizer`)
}

export function useCacheInvalidation() {
  const queryClient = useQueryClient()
  const appStore = useAppStore()

  /**
   * Invalidate all caches for a specific scenario
   * 
   * @param {number} scenarioId - Scenario ID
   * @param {number} workspaceId - Workspace ID (defaults to appStore.workspaceId)
   * @param {object} options - Options object
   * @param {boolean} options.invalidateBaseline - Also invalidate baseline scenario (for comparison mode)
   */
  const invalidateScenario = async (scenarioId, workspaceId = null, options = {}) => {
    const wsId = workspaceId || appStore.workspaceId || 0
    const { invalidateBaseline = false } = options
    
    console.log(`🔄 [Cache] Invalidating scenario ${scenarioId} (workspace ${wsId})`)
    
    // Build query key for costs (using current result type)
    const resultType = appStore.currentResultType || 'optimization'
    const costsKey = queryKeys.scenarioCosts(scenarioId, resultType, wsId)
    const cachedBefore = queryClient.getQueryData(costsKey)
    console.log(`🔄 [Cache] Cached data BEFORE invalidation for key ${JSON.stringify(costsKey)}:`, cachedBefore)
    
    // Invalidate all query types for this scenario
    await Promise.all([
      // Costs (all result types) - use partial key to invalidate all resultTypes
      queryClient.invalidateQueries({ 
        queryKey: ['scenarioCosts', scenarioId] 
      }),
      
      // Network data (all modes) - use partial key to invalidate all modes
      queryClient.invalidateQueries({ 
        queryKey: ['networkData', scenarioId] 
      }),
      
      // Facilities breakdown (all result types)
      queryClient.invalidateQueries({ 
        queryKey: ['facilitiesBreakdown', scenarioId] 
      }),
      
      // Purchase method stats
      queryClient.invalidateQueries({ 
        queryKey: ['purchaseMethodStats', scenarioId] 
      }),
      
      // Facility flows (all facilities for this scenario)
      queryClient.invalidateQueries({ 
        queryKey: ['facilityFlows'] 
      }),
      
      // Scenario bundle
      queryClient.invalidateQueries({ 
        queryKey: ['scenarioBundle', scenarioId] 
      }),
      
      // Configs
      queryClient.invalidateQueries({ 
        queryKey: ['optimizationConfig', scenarioId] 
      }),
      queryClient.invalidateQueries({ 
        queryKey: ['simulationConfig', scenarioId] 
      }),
      
      // ✅ ADD: Also invalidate baseline if requested (for comparison mode)
      ...(invalidateBaseline && scenarioId !== 1 ? [
        queryClient.invalidateQueries({ 
          queryKey: ['scenarioCosts', 1] 
        })
      ] : [])
    ])
    
    // Force refetch active queries (components currently using the data)
    await queryClient.refetchQueries({ 
      predicate: (query) => {
        const key = query.queryKey
        if (!Array.isArray(key)) return false
        
        // Refetch if it's for this scenario
        const isScenarioQuery = (
          (key[0] === 'scenarioCosts' && key[1] === scenarioId) ||
          (key[0] === 'networkData' && key[1] === scenarioId) ||
          (key[0] === 'facilitiesBreakdown' && key[1] === scenarioId) ||
          (key[0] === 'purchaseMethodStats' && key[1] === scenarioId)
        )
        
        // Also refetch baseline if requested
        const isBaselineQuery = (
          invalidateBaseline && 
          scenarioId !== 1 && 
          key[0] === 'scenarioCosts' && 
          key[1] === 1
        )
        
        return isScenarioQuery || isBaselineQuery
      },
      type: 'active'  // Only refetch queries with active observers
    })
    
    // Log cache state after invalidation
    const cachedAfter = queryClient.getQueryData(costsKey)
    console.log(`🔄 [Cache] Cached data AFTER invalidation for key ${JSON.stringify(costsKey)}:`, cachedAfter)
    console.log(`✅ [Cache] Scenario ${scenarioId} invalidated and refetched`)
  }
  
  /**
   * Invalidate costs for a scenario
   * 
   * @param {number} scenarioId - Scenario ID
   * @param {string} resultType - 'optimization' or 'simulation' (optional)
   */
  const invalidateCosts = async (scenarioId, resultType = null) => {
    const wsId = appStore.workspaceId || 0
    
    if (resultType) {
      await queryClient.invalidateQueries({ 
        queryKey: queryKeys.scenarioCosts(scenarioId, resultType, wsId) 
      })
    } else {
      // Invalidate all result types
      await queryClient.invalidateQueries({ 
        queryKey: ['scenarioCosts', scenarioId] 
      })
    }
    
    await queryClient.refetchQueries({ 
      predicate: (query) => {
        const key = query.queryKey
        return Array.isArray(key) && 
               key[0] === 'scenarioCosts' && 
               key[1] === scenarioId
      },
      type: 'active'
    })
  }
  
  /**
   * Invalidate network data for a scenario
   * 
   * @param {number} scenarioId - Scenario ID
   * @param {string} mode - 'baseline' or 'optimized' (optional)
   */
  const invalidateNetworkData = async (scenarioId, mode = null) => {
    const wsId = appStore.workspaceId || 0
    
    if (mode) {
      await queryClient.invalidateQueries({ 
        queryKey: queryKeys.networkData(scenarioId, mode, wsId) 
      })
    } else {
      // Invalidate all modes
      await queryClient.invalidateQueries({ 
        queryKey: ['networkData', scenarioId] 
      })
    }
    
    await queryClient.refetchQueries({ 
      predicate: (query) => {
        const key = query.queryKey
        return Array.isArray(key) && 
               key[0] === 'networkData' && 
               key[1] === scenarioId
      },
      type: 'active'
    })
  }
  
  /**
   * Invalidate all caches (nuclear option)
   * Use after major changes like workspace switch or logout
   */
  const invalidateAll = async () => {
    console.log('🔄 [Cache] Invalidating ALL caches')
    
    await queryClient.invalidateQueries()
    
    // Refetch all active queries
    await queryClient.refetchQueries({ type: 'active' })
    
    console.log('✅ [Cache] All caches invalidated and refetched')
  }
  
  /**
   * Invalidate after optimization completes
   * 
   * @param {number} scenarioId - Scenario ID that was optimized
   */
  const invalidateAfterOptimization = async (scenarioId) => {
    console.log(`🔄 [Cache] Invalidating after optimization for scenario ${scenarioId}`)
    
    // Set result type to optimization
    appStore.setCurrentResultType('optimization')
    
    // Invalidate all scenario data
    await invalidateScenario(scenarioId)
    
    console.log(`✅ [Cache] Post-optimization invalidation complete`)
  }
  
  /**
   * Invalidate related caches after optimization (but NOT costs)
   * 
   * Use this when optimization results are set directly in cache with setQueryData.
   * This invalidates network data, facilities, purchase methods, etc., but preserves
   * the costs cache that was just set with fresh optimization results.
   * 
   * ✅ CENTRALIZED: Uses shared internal helper function to avoid duplication
   * 
   * @param {number} scenarioId - Scenario ID that was optimized
   */
  const invalidateAfterOptimizationRun = async (scenarioId) => {
    await _invalidateAfterOptimizationRunInternal(queryClient, scenarioId, appStore)
  }
  
  /**
   * Invalidate after simulation completes
   * 
   * @param {number} scenarioId - Scenario ID that was simulated
   */
  const invalidateAfterSimulation = async (scenarioId) => {
    console.log(`🔄 [Cache] Invalidating after simulation for scenario ${scenarioId}`)
    
    // Set result type to simulation
    appStore.setCurrentResultType('simulation')
    
    // Invalidate scenario data
    await invalidateScenario(scenarioId)
    
    console.log(`✅ [Cache] Post-simulation invalidation complete`)
  }
  
  /**
   * Invalidate after data import/deletion
   * 
   * @param {number} scenarioId - Scenario ID (null for all scenarios)
   * @param {number} workspaceId - Workspace ID (defaults to appStore.workspaceId)
   */
  const invalidateAfterDataChange = async (scenarioId = null, workspaceId = null) => {
    const wsId = workspaceId || appStore.workspaceId || 0
    
    console.log(`🔄 [Cache] Invalidating after data change for scenario ${scenarioId || 'ALL'}`)
    
    if (scenarioId) {
      await invalidateScenario(scenarioId, wsId)
    } else {
      // Invalidate all scenarios
      await invalidateAll()
      
      // Also invalidate scenarios list
      await invalidateScenariosList(wsId)
    }
    
    console.log(`✅ [Cache] Post-data-change invalidation complete`)
  }
  
  /**
   * Invalidate scenarios list (for scenario list operations)
   * 
   * @param {number} workspaceId - Workspace ID (defaults to appStore.workspaceId)
   */
  const invalidateScenariosList = async (workspaceId = null) => {
    const wsId = workspaceId || appStore.workspaceId || 0
    
    console.log(`🔄 [Cache] Invalidating scenarios list for workspace ${wsId}`)
    
    await Promise.all([
      queryClient.invalidateQueries({ 
        queryKey: ['scenarios', wsId] 
      }),
      queryClient.invalidateQueries({ 
        queryKey: ['prebuiltScenarios', wsId] 
      })
    ])
    
    // Refetch active queries
    await queryClient.refetchQueries({ 
      predicate: (query) => {
        const key = query.queryKey
        return Array.isArray(key) && 
               (key[0] === 'scenarios' || key[0] === 'prebuiltScenarios')
      },
      type: 'active'
    })
    
    console.log(`✅ [Cache] Scenarios list invalidated and refetched`)
  }
  
  /**
   * Invalidate custom scenarios list
   * 
   * @param {number} workspaceId - Workspace ID (defaults to appStore.workspaceId)
   */
  const invalidateCustomScenarios = async (workspaceId = null) => {
    const wsId = workspaceId || appStore.workspaceId || 0
    
    console.log(`🔄 [Cache] Invalidating custom scenarios for workspace ${wsId}`)
    
    await queryClient.invalidateQueries({ 
      queryKey: ['customScenarios', wsId] 
    })
    
    // Refetch active queries
    await queryClient.refetchQueries({ 
      predicate: (query) => {
        const key = query.queryKey
        return Array.isArray(key) && key[0] === 'customScenarios'
      },
      type: 'active'
    })
    
    console.log(`✅ [Cache] Custom scenarios invalidated and refetched`)
  }
  
  return {
    invalidateScenario,
    invalidateCosts,
    invalidateNetworkData,
    invalidateAll,
    invalidateAfterOptimization,
    invalidateAfterSimulation,
    invalidateAfterDataChange,
    invalidateScenariosList,
    invalidateCustomScenarios,
    invalidateAfterOptimizationRun
  }
}

// ============================================================================
// STANDALONE EXPORTS - For use in mutation callbacks outside setup()
// ============================================================================

/**
 * Standalone function: Invalidate after optimization
 * 
 * Use this in mutation callbacks where you can't use the composable
 * 
 * @param {object} queryClient - Vue Query client instance
 * @param {number} scenarioId - Scenario ID that was optimized
 * @param {object} appStore - App store instance
 */
export async function invalidateAfterOptimization(queryClient, scenarioId) {
  console.log(`🔄 [Cache] Invalidating after optimization for scenario ${scenarioId}`)

  // Set result type to optimization
  appActions.setCurrentResultType('optimization')

  // Invalidate all scenario data using provided queryClient
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['scenarioCosts', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['networkData', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['facilitiesBreakdown', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['purchaseMethodStats', scenarioId] }),
    queryClient.invalidateQueries({ queryKey: ['scenarioBundle', scenarioId] })
  ])
  
  // Force refetch active queries
  await queryClient.refetchQueries({ 
    predicate: (query) => {
      const key = query.queryKey
      if (!Array.isArray(key)) return false
      return (
        (key[0] === 'scenarioCosts' && key[1] === scenarioId) ||
        (key[0] === 'networkData' && key[1] === scenarioId) ||
        (key[0] === 'facilitiesBreakdown' && key[1] === scenarioId) ||
        (key[0] === 'purchaseMethodStats' && key[1] === scenarioId)
      )
    }
  })

  console.log(`✅ [Cache] Post-optimization invalidation complete`)
}

/**
 * Standalone function: invalidate everything an optimization RUN affects.
 *
 * Named `...RelatedOnly` until 2026-09-12, when the name stopped being true.
 * It meant "related caches only, NOT costs", because both callers wrote the run's
 * POST response into the costs key first and this function was not meant to undo
 * that. That write was the defect — a different shape in a key with one shape —
 * so it is gone, costs are invalidated with everything else, and the old name
 * described behaviour that no longer exists. A name that lies about cache scope
 * is how the bug would come back.
 *
 * Differs from `invalidateAfterOptimization` in reach, not in whether costs are
 * included: this one names five keys explicitly and refetches only ACTIVE
 * queries, while that one goes through the broader `invalidateScenario`.
 * 
 * @param {object} queryClient - Vue Query client instance
 * @param {number} scenarioId - Scenario ID that was optimized
 * @param {object} appStore - App store instance
 */
export async function invalidateAfterOptimizationRun(queryClient, scenarioId) {
  await _invalidateAfterOptimizationRunInternal(queryClient, scenarioId)
}

/**
 * Standalone function: Invalidate after data import/deletion
 * 
 * Use this in mutation callbacks where you can't use the composable
 * 
 * @param {object} queryClient - Vue Query client instance
 * @param {number|null} scenarioId - Scenario ID (null for all scenarios)
 * @param {number} workspaceId - Workspace ID
 * @param {object} appStore - App store instance
 */
export async function invalidateAfterDataChange(queryClient, scenarioId, workspaceId) {
  console.log(`🔄 [Cache] Invalidating after data change for scenario ${scenarioId || 'ALL'}`)
  
  if (scenarioId) {
    // Invalidate specific scenario
    const resultType = appState.getState().currentResultType || 'optimization'
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['scenarioCosts', scenarioId] }),
      queryClient.invalidateQueries({ queryKey: ['networkData', scenarioId] }),
      queryClient.invalidateQueries({ queryKey: ['facilitiesBreakdown', scenarioId] }),
      queryClient.invalidateQueries({ queryKey: ['purchaseMethodStats', scenarioId] }),
      queryClient.invalidateQueries({ queryKey: ['scenarioBundle', scenarioId] })
    ])
    await queryClient.refetchQueries({
      predicate: (query) => {
        const key = query.queryKey
        return Array.isArray(key) && key[1] === scenarioId
      },
      type: 'active'
    })
  } else {
    // Invalidate all scenarios
    await queryClient.invalidateQueries()
    await queryClient.refetchQueries({ type: 'active' })
    
    // Also invalidate scenarios list
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['scenarios', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['prebuiltScenarios', workspaceId] })
    ])
    await queryClient.refetchQueries({
      predicate: (query) => {
        const key = query.queryKey
        return Array.isArray(key) && 
               (key[0] === 'scenarios' || key[0] === 'prebuiltScenarios')
      },
      type: 'active'
    })
  }
  
  console.log(`✅ [Cache] Post-data-change invalidation complete`)
}
